import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  LuCheck,
  LuCircleAlert,
  LuRefreshCcw,
  LuSearch,
} from 'react-icons/lu';

import EventHelpTooltip from '../../components/EventPreview/EventHelpTooltip';
import NoShowConfirmSheet from '../../components/neutral/NoShowConfirmSheet';
import { ReactComponent as TribeLogo } from '../../assets/TribeLogo.svg';

const EMPTY_GUESTS_IMAGE_URL =
  'https://res.cloudinary.com/dy7dovjqb/image/upload/v1782937253/Empty-State-No-User-Found-2--Streamline-Milano_ger4r0.svg';

function authHeaders() {
  const t = localStorage.getItem('accessToken');
  return t ? { headers: { Authorization: `Bearer ${t}` } } : {};
}

function getReservationId(item) {
  return String(item?._id || item?.reservationId || item?.ticketId || '');
}

function getGuestUser(item) {
  return item?.user || item?.guest || item?.member || item?.userId || {};
}

function guestName(item) {
  const user = getGuestUser(item);

  const first = String(user?.firstName || item?.firstName || '').trim();
  const last = String(user?.lastName || item?.lastName || '').trim();

  if (first || last) {
    const lastInitial = last ? `${last.charAt(0).toUpperCase()}.` : '';
    return `${first}${lastInitial ? ' ' + lastInitial : ''}`;
  }

  return user?.email || item?.email || 'Guest';
}

function guestAvatar(item) {
  const user = getGuestUser(item);
  return user?.profilePicUrl || item?.profilePicUrl || '/default-avatar.png';
}

function isGuestCheckedIn(item) {
  return (
    Boolean(item?.checkedIn) ||
    String(item?.attendanceStatus || '') === 'checked_in'
  );
}

function isGuestNoShow(item) {
  return (
    Boolean(item?.noShow) ||
    String(item?.attendanceStatus || '') === 'no_show'
  );
}

function hasNoShowFeeBeenCharged(item) {
  return (
    item?.noShowFeeCharged === true ||
    String(item?.penaltyChargeStatus || '') === 'charged' ||
    String(item?.noShowChargeStatus || '') === 'charged' ||
    Boolean(item?.penaltyPaymentIntentId) ||
    Boolean(item?.penaltyChargedAt) ||
    Boolean(item?.noShowPaymentIntentId) ||
    Boolean(item?.noShowChargedAt) ||
    Boolean(item?.metadata?.outstandingPenaltyPaidAt) ||
    Boolean(item?.metadata?.outstandingPenaltyPaymentIntentId)
  );
}

function canUndoNoShow(item) {
  if (!isGuestNoShow(item)) return false;
  if (hasNoShowFeeBeenCharged(item)) return false;
  if (item?.noShowCanUndo === false) return false;

  return true;
}

function checkedInTime(item) {
  if (!item?.checkedInAt) return '';

  try {
    return new Date(item.checkedInAt).toLocaleTimeString([], {
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return '';
  }
}

function noShowTime(item) {
  if (!item?.noShowMarkedAt) return '';

  try {
    return new Date(item.noShowMarkedAt).toLocaleTimeString([], {
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return '';
  }
}

function EmptyGuestsState({ query, loading, onRefresh }) {
  const hasSearch = Boolean(query.trim());

  return (
    <div style={S.emptyState}>
      <img
        src={EMPTY_GUESTS_IMAGE_URL}
        alt=""
        aria-hidden="true"
        style={S.emptyImage}
      />

      <h2 style={S.emptyTitle}>
        {hasSearch ? 'No guests found' : 'No guests yet'}
      </h2>

      <p style={S.emptyText}>
        {hasSearch
          ? 'Try a different search or refresh the guest list.'
          : 'Reservations will appear here once people reserve a spot.'}
      </p>

      <button
        type="button"
        onClick={onRefresh}
        disabled={loading}
        style={{
          ...S.emptyRefreshBtn,
          opacity: loading ? 0.6 : 1,
          cursor: loading ? 'default' : 'pointer',
        }}
      >
        <LuRefreshCcw
          size={15}
          strokeWidth={2.4}
          className={loading ? 'hostCheckInSpin' : ''}
        />
        <span>{loading ? 'Refreshing…' : 'Refresh'}</span>
      </button>
    </div>
  );
}

const S = {
  page: {
    minHeight: '100dvh',
    background: '#fff',
    color: '#0f172a',
    fontFamily:
      "'Inter', system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif",
  },

  navWrap: {
    position: 'sticky',
    top: 0,
    zIndex: 100,
    background: '#fff',
  },

  navRow: {
    height: 55,
    maxWidth: 1200,
    margin: '0 auto',
    padding: '0 12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    position: 'relative',
  },

  navButton: {
    appearance: 'none',
    background: '#fff',
    color: '#374151',
    border: '1px solid #e5e7eb',
    borderRadius: 9999,
    padding: '6px 12px',
    fontSize: '0.8rem',
    fontWeight: 600,
    lineHeight: 1,
    height: 28,
    cursor: 'pointer',
    zIndex: 1,
  },

  logoCenter: {
    position: 'absolute',
    left: '50%',
    transform: 'translateX(-50%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    pointerEvents: 'none',
  },

  logo: {
    height: 'auto',
    width: 72,
  },

  rightWrap: {
    zIndex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
  },

  main: {
    maxWidth: 720,
    width: '100%',
    margin: '0 auto',
    padding: '1px 16px 36px',
    boxSizing: 'border-box',
  },

  title: {
    margin: '0px 0 0',
    fontSize: 20,
    lineHeight: 1.02,
    fontWeight: 900,
    letterSpacing: '-0.045em',
    color: '#0B1020',
    textAlign: 'center',
  },

  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: 8,
    marginTop: 18,
  },

  statCard: {
    border: '1px solid #eef0f2',
    borderRadius: 18,
    background: '#fff',
    padding: '12px 10px',
  },

  statValue: {
    margin: 0,
    fontSize: 16,
    lineHeight: 1,
    fontWeight: 900,
    letterSpacing: '-0.04em',
    color: '#0B1020',
  },

  statLabel: {
    margin: '6px 0 0',
    fontSize: 11,
    lineHeight: 1.1,
    fontWeight: 750,
    color: '#94a3b8',
  },

  toolbar: {
    width: '100%',
    minWidth: 0,
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    margin: '18px 0 14px',
    overflow: 'hidden',
  },

  searchBox: {
    flex: '1 1 0',
    minWidth: 0,
    height: 36,
    borderRadius: 10,
    border: '1px solid rgba(0,0,0,0.12)',
    background: '#fff',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '0 12px',
    boxSizing: 'border-box',
    overflow: 'hidden',
  },

  searchIcon: {
    opacity: 0.65,
    fontSize: 18,
    color: '#0b0b0b',
    flexShrink: 0,
  },

  input: {
    border: 'none',
    outline: 'none',
    background: 'transparent',
    flex: 1,
    minWidth: 0,
    fontSize: 14,
    lineHeight: '18px',
    fontWeight: 500,
    color: '#333',
    paddingBottom: 1,
  },

  iconBtn: {
    width: 34,
    height: 34,
    borderRadius: 8,
    border: '1px solid rgba(0,0,0,0.12)',
    background: '#fff',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    WebkitTapHighlightColor: 'transparent',
    flexShrink: 0,
  },

  refreshIcon: {
    fontSize: 14,
    color: '#0b0b0b',
    opacity: 0.9,
    lineHeight: 0,
    flexShrink: 0,
  },

  errorBox: {
    borderRadius: 18,
    border: '1px solid #fee2e2',
    background: '#fff',
    color: '#991b1b',
    padding: 14,
    fontSize: 13,
    fontWeight: 750,
    marginBottom: 12,
  },

  emptyState: {
    borderRadius: 24,
    border: '0px solid #eef0f2',
    background: '#fff',
    padding: '34px 20px 36px',
    textAlign: 'center',
    color: '#0f172a',
  },

  emptyImage: {
    width: 156,
    maxWidth: '70%',
    display: 'block',
    margin: '0 auto 14px',
    opacity: 0.42,
    filter: 'grayscale(1)',
  },

  emptyTitle: {
    fontSize: '1rem',
    fontWeight: 700,
    color: '#0f172a',
    marginBottom: '0.35rem',
    letterSpacing: '-0.01em',
  },

  emptyText: {
    fontSize: '.88rem',
    color: '#6b7280',
    maxWidth: '240px',
    lineHeight: 1.45,
    margin: '0 auto',
  },

  emptyRefreshBtn: {
    height: 36,
    marginTop: 16,
    padding: '0 14px',
    borderRadius: 999,
    border: '1px solid #e5e7eb',
    background: '#fff',
    color: '#0f172a',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    fontSize: 13,
    fontWeight: 850,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },

  list: {
    display: 'grid',
    borderTop: '1px solid rgba(0,0,0,0.08)',
  },

  guestRow: {
    minHeight: 64,
    background: '#fff',
    padding: '12px 2px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 10,
    borderBottom: '1px solid rgba(0,0,0,0.08)',
  },

  guestLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    minWidth: 0,
  },

  avatar: {
    width: 42,
    height: 42,
    borderRadius: '50%',
    objectFit: 'cover',
    border: '1px solid #e5e7eb',
    flexShrink: 0,
    background: '#f8fafc',
  },

  guestName: {
    margin: 0,
    fontSize: 14,
    fontWeight: 850,
    color: '#0f172a',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },

  guestMeta: {
    margin: '4px 0 0',
    fontSize: 12,
    fontWeight: 700,
  },

  actions: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    flexShrink: 0,
  },

  primaryBtn: {
    height: 34,
    padding: '0 12px',
    borderRadius: 999,
    border: '1px solid #000',
    background: '#000',
    color: '#fff',
    fontSize: 12,
    fontWeight: 850,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },

  checkedBtn: {
    height: 34,
    padding: '0 12px',
    borderRadius: 999,
    border: '1px solid #dcfce7',
    background: '#f0fdf4',
    color: '#166534',
    fontSize: 12,
    fontWeight: 850,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
  },

  noShowBtn: {
    height: 34,
    padding: '0 12px',
    borderRadius: 999,
    border: '1px solid #fee2e2',
    background: '#fff',
    color: '#991b1b',
    fontSize: 12,
    fontWeight: 850,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },

  disabledNoShow: {
    height: 34,
    padding: '0 12px',
    borderRadius: 999,
    border: '1px solid #fee2e2',
    background: '#fef2f2',
    color: '#991b1b',
    fontSize: 12,
    fontWeight: 850,
    whiteSpace: 'nowrap',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
  },

  undoNoShowBtn: {
    height: 34,
    padding: '0 12px',
    borderRadius: 999,
    border: '1px solid #e5e7eb',
    background: '#fff',
    color: '#475569',
    fontSize: 12,
    fontWeight: 850,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },

  undoLoadingBtn: {
    height: 34,
    padding: '0 12px',
    borderRadius: 999,
    border: '1px solid #e5e7eb',
    background: '#f8fafc',
    color: '#475569',
    fontSize: 12,
    fontWeight: 850,
    whiteSpace: 'nowrap',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 7,
  },

  miniSpinner: {
    width: 13,
    height: 13,
    borderRadius: '50%',
    border: '2px solid rgba(71,85,105,0.22)',
    borderTopColor: '#475569',
    boxSizing: 'border-box',
    animation: 'hostCheckInSpin 700ms linear infinite',
  },
};

export default function NeutralGuestCheckInPage() {
  const { eventId } = useParams();
  const { state } = useLocation();
  const navigate = useNavigate();

  const api = process.env.REACT_APP_API_URL;
  const event = state?.event || null;

  const cleanEventId = useMemo(() => {
    return String(eventId || '').replace(/^neutral-/, '');
  }, [eventId]);

  const [guests, setGuests] = useState([]);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState('');
  const [undoingId, setUndoingId] = useState('');
  const [error, setError] = useState('');
  const [noShowGuest, setNoShowGuest] = useState(null);

  const checkedInCount = guests.filter(isGuestCheckedIn).length;
  const noShowCount = guests.filter(isGuestNoShow).length;
  const reservedCount = Math.max(guests.length - checkedInCount - noShowCount, 0);

  const filteredGuests = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return guests;

    return guests.filter((g) => guestName(g).toLowerCase().includes(q));
  }, [guests, query]);

  function goBackToPortal() {
    navigate(`/neutral/${cleanEventId}/portal`, {
      state: { event },
      replace: true,
    });
  }

  async function loadGuests() {
    if (!api || !cleanEventId) return;

    setLoading(true);
    setError('');

    try {
      const { data } = await axios.get(
        `${api}/host/events/${cleanEventId}/guests`,
        authHeaders()
      );

      const list = Array.isArray(data)
        ? data
        : data?.guests || data?.reservations || data?.tickets || [];

      setGuests(list);
    } catch (e) {
      console.warn(
        '[NeutralGuestCheckInPage] guest list failed:',
        e?.response?.data || e?.message
      );

      const status = e?.response?.status;

      if (status === 403) {
        setError('You do not have access to check in guests for this gathering.');
      } else {
        setError('Could not load the guest list.');
      }

      setGuests([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadGuests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, cleanEventId]);

  async function toggleCheckIn(guest) {
    const reservationId = getReservationId(guest);
    if (!reservationId || savingId || isGuestNoShow(guest)) return;

    const wasCheckedIn = isGuestCheckedIn(guest);
    const nextCheckedIn = !wasCheckedIn;

    setSavingId(reservationId);

    setGuests((prev) =>
      prev.map((g) =>
        getReservationId(g) === reservationId
          ? {
              ...g,
              checkedIn: nextCheckedIn,
              attendanceStatus: nextCheckedIn ? 'checked_in' : 'reserved',
              checkedInAt: nextCheckedIn ? new Date().toISOString() : null,
            }
          : g
      )
    );

    try {
      const { data } = await axios.patch(
        `${api}/host/reservations/${reservationId}/check-in`,
        { checkedIn: nextCheckedIn },
        authHeaders()
      );

      const updated = data?.reservation || data?.ticket || data?.guest;

      if (updated) {
        setGuests((prev) =>
          prev.map((g) =>
            getReservationId(g) === reservationId ? { ...g, ...updated } : g
          )
        );
      }
    } catch (e) {
      console.warn(
        '[NeutralGuestCheckInPage] check-in failed:',
        e?.response?.data || e?.message
      );

      setGuests((prev) =>
        prev.map((g) =>
          getReservationId(g) === reservationId
            ? {
                ...g,
                checkedIn: wasCheckedIn,
                attendanceStatus: wasCheckedIn ? 'checked_in' : 'reserved',
                checkedInAt: guest.checkedInAt || null,
              }
            : g
        )
      );

      alert('Could not update check-in. Try again.');
    } finally {
      setSavingId('');
    }
  }

  async function markNoShowGuest(guest) {
    const reservationId = getReservationId(guest);

    if (
      !reservationId ||
      savingId ||
      isGuestCheckedIn(guest) ||
      isGuestNoShow(guest)
    ) {
      return;
    }

    setSavingId(reservationId);

    try {
      const { data } = await axios.patch(
        `${api}/host/reservations/${reservationId}/no-show`,
        {},
        authHeaders()
      );

      const updated = data?.reservation || data?.ticket || data?.guest;

      if (updated) {
        setGuests((prev) =>
          prev.map((g) =>
            getReservationId(g) === reservationId ? { ...g, ...updated } : g
          )
        );
      } else {
        await loadGuests();
      }

      setNoShowGuest(null);
    } catch (e) {
      console.warn(
        '[NeutralGuestCheckInPage] no-show failed:',
        e?.response?.data || e?.message
      );

      alert(e?.response?.data?.message || 'Could not mark no-show. Try again.');
    } finally {
      setSavingId('');
    }
  }

  async function undoNoShowGuest(guest) {
    const reservationId = getReservationId(guest);

    if (
      !reservationId ||
      savingId ||
      !isGuestNoShow(guest) ||
      !canUndoNoShow(guest)
    ) {
      return;
    }

    setSavingId(reservationId);
    setUndoingId(reservationId);

    try {
      const { data } = await axios.patch(
        `${api}/host/reservations/${reservationId}/no-show/undo`,
        {},
        authHeaders()
      );

      const updated = data?.reservation || data?.ticket || data?.guest;

      if (updated) {
        setGuests((prev) =>
          prev.map((g) =>
            getReservationId(g) === reservationId ? { ...g, ...updated } : g
          )
        );
      } else {
        await loadGuests();
      }
    } catch (e) {
      console.warn(
        '[NeutralGuestCheckInPage] undo no-show failed:',
        e?.response?.data || e?.message
      );

      alert(e?.response?.data?.message || 'Could not undo no-show. Try again.');
    } finally {
      setSavingId('');
      setUndoingId('');
    }
  }

  return (
    <div style={S.page}>
      <style>
        {`
          @keyframes hostCheckInSpin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }

          @media (prefers-reduced-motion: reduce) {
            .hostCheckInSpin {
              animation: none !important;
            }
          }
        `}
      </style>

      <div style={S.navWrap}>
        <div style={S.navRow}>
          <button type="button" onClick={goBackToPortal} style={S.navButton}>
            Back
          </button>

          <div style={S.logoCenter}>
            <TribeLogo style={S.logo} />
          </div>

          <div style={S.rightWrap}>
            <EventHelpTooltip
              title="Check in guests"
              body="Use this page to mark guests as they arrive. Only mark no-show if someone did not attend. The system handles any no-show fee after the gathering ends."
            />
          </div>
        </div>
      </div>

      <main style={S.main}>
        <header>
          <h1 style={S.title}>CHECK IN GUESTS</h1>

          <div style={S.statsGrid}>
            <div style={S.statCard}>
              <p style={S.statValue}>{checkedInCount}</p>
              <p style={S.statLabel}>Checked in</p>
            </div>

            <div style={S.statCard}>
              <p style={S.statValue}>{reservedCount}</p>
              <p style={S.statLabel}>Reserved</p>
            </div>

            <div style={S.statCard}>
              <p
                style={{
                  ...S.statValue,
                  color: noShowCount ? '#991b1b' : '#0B1020',
                }}
              >
                {noShowCount}
              </p>
              <p style={S.statLabel}>No-show</p>
            </div>
          </div>
        </header>

        <div style={S.toolbar}>
          <div style={S.searchBox}>
            <LuSearch style={S.searchIcon} />

            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search guests"
              style={S.input}
            />
          </div>

          <button
            type="button"
            onClick={loadGuests}
            disabled={loading}
            style={{
              ...S.iconBtn,
              cursor: loading ? 'default' : 'pointer',
              opacity: loading ? 0.55 : 1,
            }}
            title="Refresh guest list"
            aria-label="Refresh guest list"
          >
            <LuRefreshCcw
              style={S.refreshIcon}
              className={loading ? 'hostCheckInSpin' : ''}
            />
          </button>
        </div>

        {error ? <div style={S.errorBox}>{error}</div> : null}

        {loading ? (
          <div style={S.emptyState}>Loading guests…</div>
        ) : !filteredGuests.length ? (
          <EmptyGuestsState
            query={query}
            loading={loading}
            onRefresh={loadGuests}
          />
        ) : (
          <div style={S.list}>
            {filteredGuests.map((guest) => {
              const reservationId = getReservationId(guest);
              const checkedIn = isGuestCheckedIn(guest);
              const noShow = isGuestNoShow(guest);
              const time = checkedInTime(guest);
              const noShowAt = noShowTime(guest);
              const isSaving = savingId === reservationId;
              const isUndoing = undoingId === reservationId;

              const metaText = noShow
                ? `No-show${noShowAt ? ` marked at ${noShowAt}` : ''}`
                : checkedIn
                ? `Checked in${time ? ` at ${time}` : ''}`
                : 'Reserved';

              const metaColor = noShow
                ? '#991b1b'
                : checkedIn
                ? '#166534'
                : '#64748b';

              return (
                <div key={reservationId} style={S.guestRow}>
                  <div style={S.guestLeft}>
                    <img
                      src={guestAvatar(guest)}
                      alt=""
                      onError={(e) => {
                        e.currentTarget.src = '/default-avatar.png';
                      }}
                      style={S.avatar}
                    />

                    <div style={{ minWidth: 0 }}>
                      <p style={S.guestName}>{guestName(guest)}</p>

                      <p
                        style={{
                          ...S.guestMeta,
                          color: metaColor,
                        }}
                      >
                        {metaText}
                      </p>
                    </div>
                  </div>

                  {noShow ? (
                    <div style={S.actions}>
                      <button
                        type="button"
                        disabled
                        style={{
                          ...S.disabledNoShow,
                          opacity: isUndoing ? 0.72 : 1,
                        }}
                      >
                        <LuCircleAlert size={15} />
                        No-show
                      </button>

                      {canUndoNoShow(guest) ? (
                        isUndoing ? (
                          <button type="button" disabled style={S.undoLoadingBtn}>
                            <span style={S.miniSpinner} />
                            Undoing
                          </button>
                        ) : (
                          <button
                            type="button"
                            onClick={() => undoNoShowGuest(guest)}
                            disabled={isSaving}
                            style={{
                              ...S.undoNoShowBtn,
                              opacity: isSaving ? 0.6 : 1,
                              cursor: isSaving ? 'default' : 'pointer',
                            }}
                          >
                            Undo
                          </button>
                        )
                      ) : null}
                    </div>
                  ) : checkedIn ? (
                    <button
                      type="button"
                      onClick={() => toggleCheckIn(guest)}
                      disabled={isSaving}
                      style={{
                        ...S.checkedBtn,
                        opacity: isSaving ? 0.6 : 1,
                        cursor: isSaving ? 'default' : 'pointer',
                      }}
                    >
                      <LuCheck size={15} />
                      Checked in
                    </button>
                  ) : (
                    <div style={S.actions}>
                      <button
                        type="button"
                        onClick={() => toggleCheckIn(guest)}
                        disabled={isSaving}
                        style={{
                          ...S.primaryBtn,
                          opacity: isSaving ? 0.6 : 1,
                          cursor: isSaving ? 'default' : 'pointer',
                        }}
                      >
                        Check in
                      </button>

                      <button
                        type="button"
                        onClick={() => setNoShowGuest(guest)}
                        disabled={isSaving}
                        style={{
                          ...S.noShowBtn,
                          opacity: isSaving ? 0.6 : 1,
                          cursor: isSaving ? 'default' : 'pointer',
                        }}
                      >
                        No-show
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </main>

      <NoShowConfirmSheet
        open={Boolean(noShowGuest)}
        guest={noShowGuest}
        getGuestName={guestName}
        saving={Boolean(savingId)}
        onClose={() => {
          if (!savingId) setNoShowGuest(null);
        }}
        onConfirm={() => markNoShowGuest(noShowGuest)}
      />
    </div>
  );
}